/**
 * Pure maths behind one tuning bar.
 *
 * Everything here is deliberately free of DOM and React so the render loop can
 * call it 60 times a second and the behaviour stays testable.
 */

import { clamp } from '../../lib/math'

/** Colour band of a reading, by absolute deviation in cents. */
export type TuningZone = 'in-tune' | 'close' | 'off' | 'far'

/** Upper bounds (inclusive) of each zone, in cents. */
export const ZONE_LIMITS = { 'in-tune': 3, close: 10, off: 25 } as const

/** Cents at each end of the scale. */
export const SCALE_CENTS = 50

/** Time constant of the display smoothing, in milliseconds. */
export const SMOOTHING_TAU_MS = 100

/**
 * How long a reading counts as current before it starts to fade.
 *
 * Analysis frames arrive every 20-45 ms depending on sample rate, so a reading
 * is almost always a few tens of milliseconds old by the time it is drawn.
 * Without this hold the brightness would sag between frames and recover on the
 * next one — a visible pulse on a perfectly steady note.
 */
export const HOLD_MS = 150

/** How long a bar keeps fading out after the hold, before going neutral. */
export const AFTERGLOW_MS = 1000

/** Colour band for a deviation. */
export function zoneFor(cents: number): TuningZone {
  const magnitude = Math.abs(cents)
  if (magnitude <= ZONE_LIMITS['in-tune']) return 'in-tune'
  if (magnitude <= ZONE_LIMITS.close) return 'close'
  if (magnitude <= ZONE_LIMITS.off) return 'off'
  return 'far'
}

/**
 * Position of the needle along the scale.
 *
 * @param cents - Deviation, clamped to the ends of the scale.
 * @returns Fraction in [-1, 1]; 0 is perfectly in tune.
 */
export function needleFraction(cents: number): number {
  return clamp(cents, -SCALE_CENTS, SCALE_CENTS) / SCALE_CENTS
}

/**
 * Frame-rate independent smoothing factor.
 *
 * Using a time constant rather than a fixed weight keeps the visual response
 * identical whether the loop runs at 60 or 120 fps.
 *
 * @param deltaMs - Time since the previous frame.
 * @param tauMs - Time constant; larger is smoother.
 * @returns Weight for the new value, in [0, 1].
 */
export function smoothingAlpha(deltaMs: number, tauMs: number = SMOOTHING_TAU_MS): number {
  if (deltaMs <= 0) return 0
  if (tauMs <= 0) return 1
  return 1 - Math.exp(-deltaMs / tauMs)
}

/**
 * Advance an exponential moving average.
 *
 * @param previous - Current smoothed value, or null to snap to `next`.
 */
export function advanceEma(
  previous: number | null,
  next: number,
  deltaMs: number,
  tauMs: number = SMOOTHING_TAU_MS,
): number {
  if (previous === null || !Number.isFinite(previous)) return next
  return previous + (next - previous) * smoothingAlpha(deltaMs, tauMs)
}

/** Visual state of a bar that has no current reading. */
export type FadeState = 'live' | 'afterglow' | 'idle'

/**
 * How a bar should look given the age of its last reading.
 *
 * @param ageMs - Time since the last measurement, or null if there was none.
 */
export function fadeStateFor(
  ageMs: number | null,
  holdMs: number = HOLD_MS,
  afterglowMs: number = AFTERGLOW_MS,
): FadeState {
  if (ageMs === null) return 'idle'
  if (ageMs <= holdMs) return 'live'
  return ageMs < holdMs + afterglowMs ? 'afterglow' : 'idle'
}

/**
 * Brightness for a bar: full while the reading is current, then fading out
 * linearly across the afterglow window.
 *
 * @returns Factor in [0, 1].
 */
export function afterglowOpacity(
  ageMs: number | null,
  holdMs: number = HOLD_MS,
  afterglowMs: number = AFTERGLOW_MS,
): number {
  if (ageMs === null) return 0
  if (ageMs <= holdMs) return 1
  const faded = ageMs - holdMs
  if (faded >= afterglowMs) return 0
  return 1 - faded / afterglowMs
}
